import React from 'react';
import PropTypes from 'prop-types';

import Layout from '~/components/Layout/Layout';
import { initStore } from '~/state/store';
import withState from '~/hocs/withState';
import AuthorBanner from '~/components/AuthorBanner/AuthorBanner';
import { getAuthorArticles } from '~/util/getContent';
import { forceCheck } from 'react-lazyload';
import logEvent from '~/util/analytics';
import ArticleList from '~/components/ArticleList/ArticleList';
import EmptyState from '~/components/EmptyState/EmptyState';

import Masonry from 'react-masonry-component';
import getOr from '~/util/getOr';
import find from 'lodash/find';
import throttle from 'lodash/throttle';

import masonry from '~/static/css/masonry.css';

const getAuthor = (authors, authorSlug) => find(authors, ['slug', authorSlug]);

class Author extends React.Component {
  static async getInitialProps(props) {
    const authorSlug = props.query.authorSlug;
    const authorData = await getAuthorArticles(authorSlug);

    return {
      authorData,
      authorSlug,
    };
  }

  async componentDidMount() {
    if (!this.masonry) {
      return;
    }
    const author = getAuthor(this.props.authorData.authors, this.props.authorSlug);
    const masonryLayout = this.masonry.layout.bind(this.masonry);
    this.masonryLayout = throttle(masonryLayout, 500);
    window.addEventListener('image-loaded', this.masonryLayout);

    forceCheck();
    logEvent('page_view', {
      page_type: 'author',
      page_name: author.name,
    });
  }

  componentWillReceiveProps(newProps) {
    const author = getAuthor(this.props.authorData.authors, this.props.authorSlug);
    if (newProps.authorSlug !== this.props.authorSlug)
      logEvent('page_view', {
        page_type: 'author',
        page_name: author.name,
      });
  }

  componentWillUnmount() {
    window.removeEventListener('image-loaded', this.masonryLayout);
  }

  render() {
    if (this.props.authorData.articles.length === 0) {
      return (
        <EmptyState
          title="No Results Found"
          message="Looks like this author doesn’t have any articles yet!"
          backRoute="/authors"
          buttonCaption="Return to All Authors"
        />
      );
    }

    const masonryOptions = {
      gutter: `.${masonry.gridGutter}`,
      itemSelector: `.${masonry.gridItem}`,
      columnWidth: `.${masonry.gridSizer}`,
      percentPosition: true,
      transitionDuration: 0,
    };

    const author = getAuthor(this.props.authorData.authors, this.props.authorSlug);
    const rivers = getOr(this.props.authorData, 'rivers', []);

    return (
      <Layout title={`${author.name} | theScore`}>
        <AuthorBanner author={author} rivers={rivers} />
        <Masonry
          disableImagesLoaded
          className={masonry.grid}
          options={masonryOptions}
          ref={c => {
            this.masonry = this.masonry || c.masonry;
          }}
        >
          <div className={masonry.gridSizer} />
          <div className={masonry.gridGutter} />
          <ArticleList articles={this.props.authorData.articles} />
        </Masonry>
      </Layout>
    );
  }
}

Author.propTypes = {
  authorData: PropTypes.object.isRequired,
  authorSlug: PropTypes.string.isRequired,
};

Author.displayName = 'AuthorPage';

export default withState({ createStore: initStore })(Author);
